import React, { useState } from 'react';
import { surveyTypes } from '../utils/mockData';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    surveyType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSurveyTypeChange = (value) => {
    setFormData((prev) => ({ ...prev, surveyType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate form
    if (!formData.name || !formData.email || !formData.phone || !formData.surveyType) {
      toast.error('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/quotes`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          surveyType: '',
          message: ''
        });
      }
    } catch (error) {
      // Error logging removed for production
      toast.error(error.response?.data?.detail || 'Failed to submit quote request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="quote" className="py-24 bg-gradient-to-br from-blue-900 to-blue-800">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Request a Survey Quote
          </h2>
          <p className="text-xl text-blue-100">
            Get started with your surveying project today
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <Label htmlFor="name" className="text-gray-900 font-semibold mb-2">
                Full Name *
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                required
                className="mt-2 border-gray-300 focus:border-blue-900 focus:ring-blue-900"
              />
            </div>

            {/* Email & Phone */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="email" className="text-gray-900 font-semibold mb-2">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                  required
                  className="mt-2 border-gray-300 focus:border-blue-900 focus:ring-blue-900"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-gray-900 font-semibold mb-2">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="0795254796"
                  required
                  className="mt-2 border-gray-300 focus:border-blue-900 focus:ring-blue-900"
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <Label htmlFor="location" className="text-gray-900 font-semibold mb-2">
                Project Location
              </Label>
              <Input
                id="location"
                name="location"
                type="text"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Enter project location"
                className="mt-2 border-gray-300 focus:border-blue-900 focus:ring-blue-900"
              />
            </div>

            {/* Survey Type */}
            <div>
              <Label htmlFor="surveyType" className="text-gray-900 font-semibold mb-2">
                Type of Survey *
              </Label>
              <Select value={formData.surveyType} onValueChange={handleSurveyTypeChange}>
                <SelectTrigger className="mt-2 border-gray-300 focus:border-blue-900 focus:ring-blue-900">
                  <SelectValue placeholder="Select survey type" />
                </SelectTrigger>
                <SelectContent>
                  {surveyTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message" className="text-gray-900 font-semibold mb-2">
                Project Details / Additional Information
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us about your project..."
                rows={5}
                className="mt-2 border-gray-300 focus:border-blue-900 focus:ring-blue-900"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : 'Request Survey Quote'}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
